"use client";

import { useEffect, useRef, useState } from "react";

/** Default delay before the seek pin fades out after scrub release or seek. */
export const MEDIA_PLAYER_SEEK_PIN_HIDE_DELAY_MS = 2000;

/** Playhead within this band of the scrub clamp counts as "at continuation lock". */
export const MEDIA_PLAYER_SEEK_PIN_LOCK_EPSILON = 0.35;

export function isMediaPlayerSeekAtContinuationLock(options: {
	playheadPercent: number;
	scrubClampPercent: number;
	epsilon?: number;
}): boolean {
	const {
		playheadPercent,
		scrubClampPercent,
		epsilon = MEDIA_PLAYER_SEEK_PIN_LOCK_EPSILON,
	} = options;
	if (scrubClampPercent >= 100) {
		return false;
	}
	return playheadPercent >= scrubClampPercent - epsilon;
}

export interface UseMediaPlayerSeekPinOptions {
	isScrubbing: boolean;
	/**
	 * Pointer hovering the track (or scrub hit). Pin fades in while true; leaving
	 * starts the same hide delay as scrub release.
	 */
	isHovering?: boolean;
	playheadPercent: number;
	scrubClampPercent: number;
	/** Delay after release / seek / hover-leave before pin fades out / lock clears. */
	hideDelayMs?: number;
	/** Keep the pin as the persistent playhead (debug / non-learner). */
	alwaysVisible?: boolean;
	/** External seek / skip / card-nav generation (session `seekEpoch`). */
	seekEpoch?: number;
}

export type MediaPlayerSeekPinEnterMode = "fade" | "instant";

export interface UseMediaPlayerSeekPinResult {
	/** Pin should be opacity-visible (includes hide-delay window after release). */
	pinVisible: boolean;
	/**
	 * How the pin should enter when becoming visible.
	 * Hover uses fade; scrub / seekEpoch use instant (if not already shown).
	 */
	pinEnterMode: MediaPlayerSeekPinEnterMode;
	/** Show lock glyph; otherwise book-open. Sticky for hideDelayMs after release at lock. */
	showLockIcon: boolean;
}

/**
 * Seek-pin chrome: visible while scrubbing, hovering the track, or after any
 * seekEpoch bump (skip / prev-next / keyboard); stay visible for `hideDelayMs`
 * then fade out. Hover fades in; scrub / seek appear instantly when revealing
 * from hidden. Lock icon sticks for the same window when the scrub ended
 * (or is held) at the continuation clamp.
 */
export function useMediaPlayerSeekPin({
	isScrubbing,
	isHovering = false,
	playheadPercent,
	scrubClampPercent,
	hideDelayMs = MEDIA_PLAYER_SEEK_PIN_HIDE_DELAY_MS,
	alwaysVisible = false,
	seekEpoch = 0,
}: UseMediaPlayerSeekPinOptions): UseMediaPlayerSeekPinResult {
	const [pinVisible, setPinVisible] = useState(false);
	const [pinEnterMode, setPinEnterMode] =
		useState<MediaPlayerSeekPinEnterMode>("fade");
	const [showLockIcon, setShowLockIcon] = useState(false);
	const pinHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lockHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasScrubbingRef = useRef(false);
	const wasHoveringRef = useRef(false);
	const lastSeekEpochRef = useRef(seekEpoch);

	const atLock = isMediaPlayerSeekAtContinuationLock({
		playheadPercent,
		scrubClampPercent,
	});

	function clearPinTimers() {
		if (pinHideTimerRef.current != null) {
			clearTimeout(pinHideTimerRef.current);
			pinHideTimerRef.current = null;
		}
		if (lockHideTimerRef.current != null) {
			clearTimeout(lockHideTimerRef.current);
			lockHideTimerRef.current = null;
		}
	}

	function startPinHideWindow() {
		clearPinTimers();
		if (atLock) {
			setShowLockIcon(true);
			lockHideTimerRef.current = setTimeout(() => {
				setShowLockIcon(false);
				lockHideTimerRef.current = null;
			}, hideDelayMs);
		} else {
			setShowLockIcon(false);
		}
		pinHideTimerRef.current = setTimeout(() => {
			setPinVisible(false);
			pinHideTimerRef.current = null;
		}, hideDelayMs);
	}

	useEffect(() => {
		const releasedScrub = wasScrubbingRef.current && !isScrubbing;
		const leftHover = wasHoveringRef.current && !isHovering;
		wasScrubbingRef.current = isScrubbing;
		wasHoveringRef.current = isHovering;

		if (isScrubbing || isHovering) {
			clearPinTimers();
			setPinEnterMode(isScrubbing ? "instant" : "fade");
			setPinVisible(true);
			if (isScrubbing) {
				setShowLockIcon(atLock);
			}
			return;
		}

		if (!releasedScrub && !leftHover) {
			return;
		}

		setPinVisible(true);
		startPinHideWindow();
	}, [isScrubbing, isHovering, atLock, hideDelayMs]);

	useEffect(() => {
		if (seekEpoch === lastSeekEpochRef.current) {
			return;
		}
		lastSeekEpochRef.current = seekEpoch;
		if (alwaysVisible || isScrubbing || isHovering) {
			return;
		}
		setPinEnterMode("instant");
		setPinVisible(true);
		startPinHideWindow();
	}, [
		seekEpoch,
		alwaysVisible,
		isScrubbing,
		isHovering,
		atLock,
		hideDelayMs,
	]);

	useEffect(() => {
		return () => {
			clearPinTimers();
		};
	}, []);

	return {
		pinVisible: alwaysVisible || pinVisible,
		pinEnterMode: alwaysVisible ? "instant" : pinEnterMode,
		showLockIcon,
	};
}
